// src/services/employeeSurveyReportService.js
import { employeeSurveyReportsAPI } from './api';
import { downloadBlobResponse, extensionForFormat } from '../utils/downloadFile';

export const employeeSurveyReportService = {
  // Get surveys for dropdown
  async getSurveys() {
    try {
      console.log('🔄 Fetching surveys for reports');
      const response = await employeeSurveyReportsAPI.getSurveys();
      console.log('✅ Surveys loaded:', response.data.length, 'surveys');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching surveys:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw new Error('Failed to load surveys: ' + (error.response?.data || error.message));
    }
  },

  // Get employees by filters
  async getEmployees(filters) {
    try {
      console.log('🔄 Fetching employees with filters:', filters);

      if (!filters.surveyId) {
        throw new Error('Survey ID is required');
      }

      const response = await employeeSurveyReportsAPI.getEmployees(filters);
      console.log('✅ Employees loaded:', response.data.length, 'employees');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching employees:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url,
        method: error.config?.method
      });

      // Provide specific error messages based on status code
      if (error.response?.status === 404) {
        throw new Error('No employees found for the selected criteria');
      } else if (error.response?.status === 500) {
        throw new Error('Server error while loading employees. Please try again.');
      } else if (error.response?.status === 400) {
        throw new Error('Invalid request parameters: ' + (error.response?.data || 'Bad request'));
      } else {
        throw new Error('Failed to load employees: ' + (error.message || 'Unknown error'));
      }
    }
  },

  // Generate report
  async generateReport(request) {
    try {
      console.log('🔄 Generating employee survey report:', request);

      if (!request.surveyId) {
        throw new Error('Survey ID is required');
      }

      // Empty employeeIds means all employees in the survey/company; the report UI is intentionally aggregated and does not expose employee names.

      const response = await employeeSurveyReportsAPI.generateReport(request);
      console.log('✅ Report generated successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error generating report:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });

      if (error.response?.status === 500) {
        throw new Error('Server error while generating report. Please try again.');
      } else {
        throw new Error('Failed to generate report: ' + (error.message || 'Unknown error'));
      }
    }
  },

  // Export report to PDF/Excel, downloaded straight to the browser.
  // This used to be a stub that never called the backend at all.
  async exportReport(request, format = 'excel') {
    try {
      console.log('🔄 Exporting report:', request, 'format:', format);

      if (!request.surveyId) {
        throw new Error('Survey ID is required');
      }

      const response = await employeeSurveyReportsAPI.exportReport(request, format);
      downloadBlobResponse(response, `employee-survey-report-${request.surveyId}.${extensionForFormat(format)}`);

      console.log('✅ Report exported successfully');
      return { success: true, message: 'Report exported successfully' };
    } catch (error) {
      console.error('❌ Error exporting report:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      // error.response?.data now carries the real backend message (see the
      // api.js response interceptor's blob-decoding fix) instead of the
      // generic axios "Request failed with status code 500". The caller
      // (EmployeeSurveyReport.jsx) already prefixes this with "Failed to
      // export report: ", so this throws the bare detail instead of
      // prefixing it again here too.
      throw new Error(error.response?.data || error.message);
    }
  }
};
